<template>
  <checkout-section-card>
    <div class="mb-5 flex items-center justify-between gap-3">
      <h2
        class="flex items-center gap-2.5 text-xl font-semibold uppercase tracking-wide text-mouse"
      >
        <fa-icon :icon="faLocationDot" class="size-5 text-rhino" />
        {{ sectionTitle }}
      </h2>
    </div>

    <!-- Skeleton loading state -->
    <div
      v-if="checkoutStore.isLoading && checkoutStore.locations.length === 0"
      class="grid grid-cols-1 gap-x-3 gap-y-4 sm:grid-cols-2 lg:grid-cols-3 animate-pulse"
    >
      <div
        v-for="i in 6"
        :key="i"
        class="flex items-center gap-2 rounded-lg bg-depth-1 px-4 py-2"
      >
        <div class="h-4 w-6 rounded-md bg-depth-2 shrink-0"></div>
        <div class="min-w-0">
          <div class="h-4 w-20 rounded-md bg-depth-2"></div>
          <div class="mt-1 h-3 w-14 rounded-md bg-depth-2"></div>
        </div>
      </div>
    </div>

    <!-- Location grid -->
    <div
      v-else
      role="radiogroup"
      class="grid grid-cols-1 gap-x-3 gap-y-4 sm:grid-cols-2 lg:grid-cols-3"
    >
      <button
        v-for="location in sortedLocations"
        :key="location.id"
        type="button"
        role="radio"
        class="relative flex flex-col gap-1 rounded-lg border-2 px-4 py-2 text-left transition-all duration-200"
        :class="locationCardClass(location)"
        :aria-checked="checkoutStore.selectedLocationId === location.id"
        :disabled="isSoldOut(location)"
        :aria-disabled="isSoldOut(location)"
        :data-testid="`checkout-location-${location.id}`"
        @click="onSelect(location)"
      >
        <!-- Row 1: flag + country / city -->
        <div
          class="flex min-w-0 items-center gap-3"
          :class="{ 'opacity-40': isSoldOut(location) }"
        >
          <common-flag-icon :iso-code="location.country" class="shrink-0" />
          <div class="min-w-0 leading-tight">
            <span
              class="block truncate"
              :class="
                checkoutStore.selectedLocationId === location.id
                  ? 'text-brand'
                  : 'text-mouse'
              "
            >
              {{ countryName(location.country) }}
            </span>
            <span class="block truncate text-sm font-normal text-rhino">
              {{ location.name }}
            </span>
          </div>
        </div>

        <!-- Ping badge (top-right, outline style) -->
        <client-only>
          <span
            v-if="latencyByLocationId.has(location.id)"
            class="absolute -top-3 -right-2 rounded-full border bg-depth-0 px-2 py-0.5 text-sm leading-tight"
            :class="latencyBadgeClass(latencyByLocationId.get(location.id)!)"
          >
            {{ latencyByLocationId.get(location.id) }}ms
          </span>
        </client-only>

        <!-- BEST badge (top-left, overlapping border) -->
        <span
          v-if="!isSoldOut(location) && isBestLatency(location.id)"
          class="absolute -top-3 -left-2 rounded-full bg-success px-2 py-0.5 text-sm font-bold uppercase leading-tight text-onyx"
        >
          {{ t('checkout/configure/location/best') }}
        </span>

        <!-- Sold out badge (top-left) -->
        <span
          v-if="isSoldOut(location)"
          class="absolute -top-3 -left-2 rounded-full bg-danger px-2 py-0.5 text-sm font-bold uppercase leading-tight text-mouse"
          :data-testid="`checkout-location-sold-out-badge-${location.id}`"
        >
          {{ t('checkout/configure/location/sold_out') }}
        </span>
      </button>
    </div>
    <!-- Region mismatch warning -->
    <div
      v-if="checkoutStore.regionMismatch"
      class="mt-3 flex flex-col gap-2 rounded-lg border-2 border-warning/40 bg-warning/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <p class="text-warning">
        {{
          t('checkout/configure/location/region_warning', {
            storeName: regionMismatchStoreName,
          })
        }}
      </p>
      <button
        type="button"
        class="shrink-0 rounded-md bg-warning px-3 py-1.5 text-sm font-semibold uppercase tracking-wider text-onyx hover:bg-warning/90"
        :disabled="switchingRegion"
        @click="switchRegion"
      >
        {{ t('checkout/configure/location/region_switch') }}
      </button>
    </div>
  </checkout-section-card>
</template>

<script setup lang="ts">
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import type { OrderLocation } from 'gportal-grpc/gportal/api/payment/v1/order_pb';

import {
  extractDatacenterSlug,
  LATENCY_DANGER_MS,
  LATENCY_WARNING_MS,
  optionCardClass,
} from '~/helpers/checkout';
import { GpRegion } from '~/plugins/grpc/universal/types';
import type { LatencyMeasurement } from '~/store/types';

const { t, locale } = useI18n();

const regionNames = computed(
  () => new Intl.DisplayNames([locale.value], { type: 'region' }),
);

function countryName(isoCode: string): string {
  try {
    return regionNames.value.of(isoCode) ?? isoCode;
  } catch {
    return isoCode;
  }
}
const checkoutStore = useCheckoutStore();
const locationStore = useLocationStore();
const authStore = useAuthStore();

const sectionTitle = computed(() => checkoutStore.summaryLocationLabel);

// Truth check for sold-out: backend response says unavailable OR backend has
// rejected this id with FailedPrecondition + "is sold out" via the store's
// overlay set. The overlay covers the absent-from-response case where the
// id isn't even present in `locations` after a refetch.
function isSoldOut(loc: OrderLocation): boolean {
  return !loc.available || checkoutStore.soldOutLocationIds.has(loc.id);
}

// Maps location.id (WI key, e.g. "EUW") → datacenter slug (e.g. "fra01")
// by extracting the subdomain from the latency endpoint URL. This bridges
// the gap between the latency plugin (which stores measurements by datacenter
// slug) and the checkout store (which uses WI keys as location IDs).
const locationSlugMap = computed(() => {
  const map = new Map<string, string>();
  for (const loc of checkoutStore.locations) {
    const slug = extractDatacenterSlug(loc.latencyEndpointUrl);
    if (slug) map.set(loc.id, slug);
  }
  return map;
});

const latencyBySlug = computed(() => {
  return new Map(
    locationStore.latencyMeasurements.map((m: LatencyMeasurement) => [
      m.slug,
      m.latency,
    ]),
  );
});

const sortedLocations = computed(() => {
  const locs = [...checkoutStore.locations];
  if (latencyBySlug.value.size === 0) return locs;

  const slugMap = locationSlugMap.value;
  return locs.sort((a, b) => {
    const slugA = slugMap.get(a.id) ?? a.id;
    const slugB = slugMap.get(b.id) ?? b.id;
    const latA = latencyBySlug.value.get(slugA);
    const latB = latencyBySlug.value.get(slugB);
    if (latA !== undefined && latB !== undefined) return latA - latB;
    if (latA !== undefined) return -1;
    if (latB !== undefined) return 1;
    return 0;
  });
});

// Best latency among available locations only (sold-out locations are excluded).
// Measurements may be keyed by datacenter slug (from the latency plugin) or by
// WI key (from the checkout page), so we resolve through locationSlugMap.
const bestAvailableLatencyLocation = computed(() => {
  const measurements = locationStore.latencyMeasurements;
  if (measurements.length === 0) return undefined;

  const slugMap = locationSlugMap.value;

  // Build reverse map: datacenter slug → location.id (only available locations,
  // excluding any flagged sold-out by the backend overlay set).
  const slugToLocationId = new Map<string, string>();
  for (const loc of checkoutStore.locations) {
    if (!isSoldOut(loc)) {
      const slug = slugMap.get(loc.id);
      if (slug) slugToLocationId.set(slug, loc.id);
      slugToLocationId.set(loc.id, loc.id);
    }
  }

  const available = measurements.filter((m) => slugToLocationId.has(m.slug));
  if (available.length === 0) return undefined;

  const best = available.reduce((prev, current) =>
    prev.latency < current.latency ? prev : current,
  );
  return slugToLocationId.get(best.slug);
});

// Track whether the user manually picked a location. Reactively follows the
// store's restoredFromStorage flag so a previous session's choice is respected,
// even if restoration completes after this component initializes.
// Bidirectional: when restoredFromStorage goes false (e.g. resetConfigSelections),
// userPickedLocation resets so the auto-select watcher can pick best latency.
const userPickedLocation = ref(false);
watch(
  () => checkoutStore.restoredFromStorage,
  (restored) => {
    userPickedLocation.value = restored;
  },
  { immediate: true },
);

watch(
  [() => checkoutStore.locations, bestAvailableLatencyLocation],
  ([locations, bestLatency]) => {
    if (locations.length === 0 || userPickedLocation.value) return;

    const available = locations.filter((l) => !isSoldOut(l));
    if (available.length === 0) return;

    if (checkoutStore.selectedLocationId === null) {
      if (bestLatency && available.some((l) => l.id === bestLatency)) {
        checkoutStore.selectLocation(bestLatency);
      } else {
        checkoutStore.selectLocation(available[0].id);
      }
      return;
    }

    // Upgrade to best latency when measurements arrive
    if (
      bestLatency &&
      bestLatency !== checkoutStore.selectedLocationId &&
      available.some((l) => l.id === bestLatency)
    ) {
      checkoutStore.selectLocation(bestLatency);
    }
  },
  { immediate: true },
);

function isBestLatency(locationId: string): boolean {
  return bestAvailableLatencyLocation.value === locationId;
}

const latencyByLocationId = computed(() => {
  const map = new Map<string, number>();

  for (const loc of checkoutStore.locations) {
    const slug = locationSlugMap.value.get(loc.id) ?? loc.id;
    const latency = latencyBySlug.value.get(slug);
    if (latency !== undefined) map.set(loc.id, latency);
  }

  return map;
});

function latencyBadgeClass(latency: number): string {
  if (latency > LATENCY_DANGER_MS) return 'border-danger text-danger-fg';
  if (latency > LATENCY_WARNING_MS) return 'border-warning text-warning';
  return 'border-success text-success';
}

function locationCardClass(location: OrderLocation): string {
  return optionCardClass(
    checkoutStore.selectedLocationId === location.id,
    isSoldOut(location),
  );
}

function onSelect(location: OrderLocation): void {
  if (isSoldOut(location)) return;
  userPickedLocation.value = true;
  checkoutStore.selectLocation(location.id);
}

// --- Region mismatch (UI helpers) ---

const regionMismatchStoreName = computed(() => {
  return checkoutStore.selectedLocationRegion === GpRegion.EUR
    ? 'European'
    : 'International';
});

const { switchRegion: doRegionSwitch, isSwitching: switchingRegion } =
  useRegionSwitch();
const { pushErrorNotification } = useNotifications();

async function switchRegion() {
  const targetRegion = checkoutStore.selectedLocationRegion;
  if (!targetRegion) return;

  try {
    if (authStore.isAuthenticated) {
      await doRegionSwitch(targetRegion);
    } else {
      // Unauthenticated users: switch currency directly.
      const newCurrency = targetRegion === GpRegion.EUR ? 'EUR' : 'USD';
      const currencyStore = useCurrencyStore();
      currencyStore.setCurrency(newCurrency, true);
    }

    // The activeRegion / currency watchers on the checkout page will
    // automatically re-fetch order information with the new currency.
  } catch {
    pushErrorNotification({
      title: t('checkout/configure/location/region_switch_error_title'),
      message: t('checkout/configure/location/region_switch_error'),
    });
  }
}
</script>
